/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { Apps, AssociableApps, Component } from 'types/api-data';
import { IDependencies, Service } from 'types/services';

export const hasChangesInTwoAppArr = (
  applications: AssociableApps[] | Apps[],
  dependencies: IDependencies[]
): boolean => {
  // Extract application's names
  const applicationsNames = applications.map((app) => app.name).sort();
  const sourceNames = dependencies
    .filter((dep) => dep.dependency_type === 'comprises')
    .map((dep) => dep.source_name)
    .sort();

  // Check if lengths are different
  if (applicationsNames.length !== sourceNames.length) {
    return true; // If lengths differ, there are changes
  }

  // Check if every application name matches a source_name
  return !applicationsNames.every((name, index) => name === sourceNames[index]);
};

export const hasServiceChanged = (
  selectedService: Service | null,
  dependencies: IDependencies[]
): boolean => {
  // Find existing served_by dependency
  const existingServiceDep = dependencies.find(
    (dep) => dep.dependency_type === 'served_by'
  );

  // If there's no selected service and no existing dependency, no change
  if (!selectedService && !existingServiceDep) {
    return false;
  }

  // If one exists and the other doesn't, there's a change
  if (
    (!selectedService && existingServiceDep) ||
    (selectedService && !existingServiceDep)
  ) {
    return true;
  }

  // If both exist, compare their component names
  return selectedService?.component_name !== existingServiceDep?.target_name;
};

export const hasWorkflowChanged = (
  selectedWorkflows: Component[],
  dependencies: IDependencies[]
): boolean => {
  // Extract workflow names
  const workflowNames = selectedWorkflows
    .map((workflow) => workflow.component_name)
    .sort();

  const sourceNames = dependencies
    .filter((dep) => dep.dependency_type === 'consumes')
    .map((dep) => dep.source_name)
    .sort();

  // Check if the lengths are different
  if (workflowNames.length !== sourceNames.length) {
    return true; // If lengths differ, there are changes
  }

  // Check if every application name matches a source_name
  return !workflowNames.every((name, index) => name === sourceNames[index]);
};

// Combined checker for both apps and service changes
export const hasDependencyChanges = (
  applications: AssociableApps[] | Apps[],
  selectedService: Service | null,
  dependencies: IDependencies[],
  selectedWorkflows: Component[]
): boolean => {
  return (
    hasChangesInTwoAppArr(applications, dependencies) ||
    hasServiceChanged(selectedService, dependencies) ||
    hasWorkflowChanged(selectedWorkflows, dependencies)
  );
};
